/**
 * The Operations Hub's map tab (docs/specs/portal/operations-hub.md §14 to §19; MB's prototype v3): the project panel
 * (aircraft-type filter, project cards), the map, the line-state legend and the Tracking assets panel. Every figure
 * and word comes from `@teslyn/domain` `ops-hub.ts`: aircraft and colours (decision 424), outlines and pins
 * (decision 425), progress (`opsProgress`), the estimated finish (decision 421), status words (decision 423) and the
 * tracking assets (decision 420). Crew (decision 422) waits for the staff register (`HubCrewLoader`).
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { cloudRouteFetcher } from '../map/publicRoute.ts';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  HUB_AIRCRAFT_TYPE_LABEL,
  HUB_AIRCRAFT_TYPES,
  HUB_FINISH_WINDOW_DAYS,
  HUB_LINES_MIN_ZOOM,
  HUB_TYPE_COLOUR,
  LINE_STATE_LEGEND,
  BOUNDARY_LEGEND,
  CONTRACT_AREA_LEGEND,
  type LegendEntry,
  baseMapKind,
  cloudTrafficStatusText,
  hubSourceStrip,
  hubTrafficAreas,
  hubEstimatedFinish,
  hubFilter,
  hubProject,
  standDownDays,
  hubShows,
  hubTracking,
  kmText,
  lineStateFeatures,
  photoFeatures,
  type ProjectPhoto,
  type BaseLayerId,
  type HubAircraftType,
  type FleetMrRow,
  type HubFinish,
  type HubFleetAircraft,
  type HubPlan,
  type HubProject,
  type MapFeature,
  type OpsLine,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { SelectField } from '../components/Fields.tsx';
import { HubMrList } from './HubMrList.tsx';
import { ErrorState, Loading } from '../components/States.tsx';
import { loadProjectPlan } from '../portal/api.ts';
import { WEB_BASE_LAYERS, textNode } from '../map/leaflet.ts';
import { FireControls, useCloudFires, useFireChoice } from '../map/cloudFires.tsx';
import { usePublicTraffic } from '../map/publicTraffic.ts';
import { loadOpsLines } from './api.ts';
import {
  loadHubPhotos,
  loadHubFleet,
  loadHubMr,
  loadHubProjects,
  loadHubTracking,
  type HubCrewLoader,
  type HubCrewMember,
  type HubProjectLoaded,
  type HubTrackingRows,
} from './hubApi.ts';
import { HubMap } from './HubMap.tsx';
import { TrackingAssetsPanel } from './TrackingAssetsPanel.tsx';

export interface HubViewProps {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  /** The viewer's local date. */
  readonly today: string;
  /** Now, for the trackers' ages; tests pass a fixed one. */
  readonly nowMs?: () => number;
  /** The staff register's project crew (decision 422); absent until `p4/staff-register` lands. */
  readonly loadCrew?: HubCrewLoader;
}

type Filter = HubAircraftType | 'all';
const errorText = (e: unknown): string => (e instanceof Error ? e.message : String(e));
/** Plans load a few projects at a time, not all at once. */
const PLAN_CONCURRENCY = 3;
/** The cloud poller stores positions every 1 to 2 minutes (decision 419). */
const TRACKING_REFRESH_MS = 60_000;
const DEFAULT_HOURS = 12;

const minusDays = (iso: string, n: number): string =>
  new Date(Date.parse(`${iso}T00:00:00Z`) - n * 86_400_000).toISOString().slice(0, 10);

const pctText = (n: number): string => `${n} %`;

/** The words of a card and a popup (the same lines in both). */
export function hubLines(
  p: HubProject,
  finish: HubFinish | null,
  crew: readonly HubCrewMember[] | null,
): {
  readonly progress: string | null;
  readonly km: string | null;
  readonly report: string;
  readonly finish: string | null;
  readonly crew: string | null;
} {
  return {
    progress:
      p.progress === null
        ? null
        : p.hasLines
          ? `${pctText(p.progress.percentComplete)} accepted`
          : 'No planned lines yet',
    km:
      p.progress && p.hasLines
        ? `${kmText(p.progress.acceptedKm)} accepted · ${kmText(p.progress.remainingKm)} remaining`
        : null,
    report: p.latestReport ? `Latest report: ${p.latestReport}` : 'No report yet',
    finish: p.hasLines && finish ? finish.text : null,
    crew: crew && crew.length > 0 ? `Crew: ${crew.map((c) => `${c.name} (${c.role})`).join(', ')}` : null,
  };
}

/** A pin's popup: project, client and aircraft, status, progress, latest report, estimated finish (text nodes). */
export function hubPopup(
  p: HubProject,
  finish: HubFinish | null,
  crew: readonly HubCrewMember[] | null,
): HTMLElement {
  const el = document.createElement('div');
  el.className = 'hub-popup';
  const l = hubLines(p, finish, crew);
  el.append(textNode(`${p.number} · ${p.title}`, 'heading hub-popup__title'));
  for (const line of [
    p.subtitle,
    `Status: ${p.statusWord}`,
    l.progress ?? 'Loading progress…',
    l.km,
    l.report,
    l.finish,
    l.crew,
  ]) {
    if (line) el.append(textNode(line, 'caption hub-popup__line'));
  }
  return el;
}

export function HubView({ sb, tenantId, today, nowMs = Date.now, loadCrew }: HubViewProps): ReactElement {
  // a public aircraft's planned route on a click (R160; the Hub is staff only)
  const routeOf = useMemo(() => cloudRouteFetcher(sb, tenantId), [sb, tenantId]);
  const [data, setData] = useState<{ projects: HubProjectLoaded[]; fleet: HubFleetAircraft[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    setError(null);
    Promise.all([loadHubProjects(sb, tenantId), loadHubFleet(sb, tenantId)])
      .then(
        ([projects, fleet]) =>
          live && setData({ projects: projects.filter((p) => hubShows(p.status)), fleet }),
      )
      .catch((e: unknown) => live && setError(errorText(e)));
    return () => {
      live = false;
    };
  }, [sb, tenantId, attempt]);

  // R239: the hours to the next MR, each aircraft; a failure here leaves the rest of the Hub as it is
  const [mr, setMr] = useState<readonly FleetMrRow[] | string | null>(null);
  useEffect(() => {
    let live = true;
    loadHubMr(sb, tenantId)
      .then((rows) => live && setMr(rows))
      .catch((e: unknown) => live && setMr(errorText(e)));
    return () => {
      live = false;
    };
  }, [sb, tenantId, attempt]);

  // each project's plan and Line review, a few at a time
  const [plans, setPlans] = useState<Readonly<Record<string, HubPlan | string>>>({});
  useEffect(() => {
    if (!data) return undefined;
    let live = true;
    const queue = data.projects.map((p) => p.id);
    const next = async (): Promise<void> => {
      for (let id = queue.shift(); id !== undefined && live; id = queue.shift()) {
        const pid = id;
        try {
          const plan = await loadProjectPlan(sb, pid);
          if (live) setPlans((m) => ({ ...m, [pid]: plan }));
        } catch (e: unknown) {
          if (live) setPlans((m) => ({ ...m, [pid]: errorText(e) }));
        }
      }
    };
    void Promise.all(Array.from({ length: PLAN_CONCURRENCY }, next));
    return () => {
      live = false;
    };
  }, [sb, data]);

  // the last 14 days of line statuses, for the estimated finish (decision 421)
  const [recent, setRecent] = useState<OpsLine[] | null>(null);
  useEffect(() => {
    let live = true;
    loadOpsLines(sb, tenantId, { from: minusDays(today, HUB_FINISH_WINDOW_DAYS - 1), to: today })
      .then((l) => live && setRecent(l))
      .catch(() => live && setRecent([]));
    return () => {
      live = false;
    };
  }, [sb, tenantId, today]);

  // crew (decision 422), once a loader exists
  const [crew, setCrew] = useState<ReadonlyMap<string, HubCrewMember[]> | null>(null);
  useEffect(() => {
    if (!loadCrew || !data) return undefined;
    let live = true;
    loadCrew(data.projects.map((p) => p.id))
      .then((c) => live && setCrew(c))
      .catch(() => live && setCrew(null));
    return () => {
      live = false;
    };
  }, [loadCrew, data]);

  const projects = useMemo(() => {
    if (!data) return [];
    return data.projects.map((p) => {
      const plan = plans[p.id];
      return hubProject(p, data.fleet, typeof plan === 'object' ? plan : null);
    });
  }, [data, plans]);
  const firstDay = useMemo(() => new Map((data?.projects ?? []).map((p) => [p.id, p.firstDay])), [data]);
  // decision 491: Suspended days and the stand-down period are left out of the estimated finish's window
  const leftOut = useMemo(
    () =>
      new Map(
        (data?.projects ?? []).map((p) => [
          p.id,
          [...(p.suspendedDays ?? []), ...standDownDays(p.standDownFrom, p.standDownTo)],
        ]),
      ),
    [data],
  );
  const finishes = useMemo(() => {
    const out = new Map<string, HubFinish>();
    if (!recent) return out;
    for (const p of projects) {
      if (!p.progress) continue;
      out.set(
        p.id,
        hubEstimatedFinish({
          lines: recent,
          projectId: p.id,
          firstDay: firstDay.get(p.id) ?? null,
          today,
          remainingKm: p.progress.remainingKm,
          standDownDays: leftOut.get(p.id) ?? [],
          kmCompletedBlocks: p.progress.kmCompletedBlocks,
        }),
      );
    }
    return out;
  }, [projects, recent, firstDay, leftOut, today]);

  const [filter, setFilter] = useState<Filter>('all');
  const shown = useMemo(() => hubFilter(projects, filter), [projects, filter]);
  // the outlines the map draws, in the legend at every zoom (decision 731)
  const outlineLegend = useMemo<readonly LegendEntry[]>(
    () => [
      ...(shown.some((p) => p.outlineKind === 'blocks') ? [BOUNDARY_LEGEND] : []),
      ...(shown.some((p) => p.outlineKind === 'contract') ? [CONTRACT_AREA_LEGEND] : []),
    ],
    [shown],
  );

  // project photos with a place (decision 429): pins drawn with the project's lines
  const [photos, setPhotos] = useState<ReadonlyMap<string, ProjectPhoto[]>>(new Map());
  useEffect(() => {
    let live = true;
    loadHubPhotos(sb, tenantId)
      .then((m) => live && setPhotos(m))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [sb, tenantId]);

  // line features and photo pins, built once per loaded plan
  const lineCache = useRef(
    new Map<string, { plan: HubPlan; photos: ProjectPhoto[] | undefined; features: readonly MapFeature[] }>(),
  );
  const linesOf = useCallback(
    (id: string): readonly MapFeature[] | null => {
      const plan = plans[id];
      if (typeof plan !== 'object') return null;
      const own = photos.get(id);
      const hit = lineCache.current.get(id);
      if (hit && hit.plan === plan && hit.photos === own) return hit.features;
      const epsg = data?.projects.find((p) => p.id === id)?.epsg ?? null;
      const features = [
        ...lineStateFeatures(plan.statuses, plan.plan, plan.blocks, { epsg }).features,
        ...photoFeatures(own ?? []).features,
      ];
      lineCache.current.set(id, { plan, photos: own, features });
      return features;
    },
    [plans, data, photos],
  );

  const popupFor = useCallback(
    (p: HubProject) => hubPopup(p, finishes.get(p.id) ?? null, crew?.get(p.id) ?? null),
    [finishes, crew],
  );

  const [base, setBase] = useState<BaseLayerId>('hybrid');
  const [focus, setFocus] = useState<{ box: NonNullable<HubProject['box']>; nonce: number } | null>(null);
  const [zoom, setZoom] = useState(4);

  // ---- tracking assets (decisions 419, 420)
  const [hours, setHours] = useState(DEFAULT_HOURS);
  const [rows, setRows] = useState<HubTrackingRows | null>(null);
  const [trackingError, setTrackingError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setTick((n) => n + 1), TRACKING_REFRESH_MS);
    return () => clearTimeout(t);
  }, [tick]);
  useEffect(() => {
    let live = true;
    loadHubTracking(sb, tenantId, hours)
      .then((r) => {
        if (!live) return;
        setRows(r);
        setTrackingError(null);
      })
      .catch((e: unknown) => live && setTrackingError(errorText(e)));
    return () => {
      live = false;
    };
  }, [sb, tenantId, hours, tick]);
  // public air traffic around the active projects (parity C7): each read keeps it asked for; the first asks at once,
  // then every 8 s until it shows, then each minute (decision 828)
  const trafficTarget = useMemo(() => ({ kind: 'hub' as const, tenantId }), [tenantId]);
  const traffic = usePublicTraffic(sb, trafficTarget, nowMs);
  // fire on the map (decision 806): the agencies' fires and DEA's hot spots, read by Teslyn Cloud's fire poller
  const [fireChoice, setFireChoice] = useFireChoice();
  const fires = useCloudFires(sb, tenantId, fireChoice, nowMs);
  const [shownGroups, setShownGroups] = useState<string[]>([]);
  const tracking = useMemo(
    () =>
      rows
        ? hubTracking({
            assets: rows.assets,
            rows: rows.rows,
            sos: rows.sos,
            settings: rows.settings,
            nowMs: nowMs(),
            offsetH: -new Date().getTimezoneOffset() / 60,
            shownGroups,
            publicRows: traffic?.rows ?? [],
          })
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nowMs is read at each refresh of the rows
    [rows, shownGroups, traffic],
  );
  const trafficText = traffic?.status ? cloudTrafficStatusText(traffic.status, nowMs()) : null;
  // the strip of public traffic sources under the map (MB: feedback on whether the public feeds work)
  const trafficAreas = useMemo(() => hubTrafficAreas(data?.projects ?? []), [data]);
  const sources = useMemo(
    () =>
      traffic?.status
        ? hubSourceStrip({ status: traffic.status, rows: traffic.rows, areas: trafficAreas, nowMs: nowMs() })
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nowMs is read at each refresh of the traffic
    [traffic, trafficAreas],
  );
  const [follow, setFollow] = useState<string | null>(null);
  const [followStopped, setFollowStopped] = useState<string | null>(null);
  const [flyTo, setFlyTo] = useState<{ id: string; nonce: number } | null>(null);

  if (error) return <ErrorState message={error} onRetry={() => setAttempt((n) => n + 1)} />;
  if (!data) return <Loading what="the Operations Hub" />;

  const linesShown = zoom >= HUB_LINES_MIN_ZOOM;
  return (
    <div className="hub">
      <aside className="hub-panel" aria-label="Projects">
        <SelectField
          label="Aircraft type"
          value={filter}
          onChange={setFilter}
          options={[
            ['all', 'All aircraft types'] as const,
            ...HUB_AIRCRAFT_TYPES.map((t) => [t, HUB_AIRCRAFT_TYPE_LABEL[t]] as const),
          ]}
        />
        <ul className="legend hub-panel__types" aria-label="Aircraft type colours">
          {HUB_AIRCRAFT_TYPES.map((t) => (
            <li key={t} className="legend__item caption">
              <span
                className="hub-swatch"
                style={{ background: `var(--${HUB_TYPE_COLOUR[t]})` }}
                aria-hidden="true"
              />
              {HUB_AIRCRAFT_TYPE_LABEL[t]}
            </li>
          ))}
        </ul>
        <HubMrList rows={mr} />
        {projects.length === 0 && <p className="body">No projects yet.</p>}
        {projects.length > 0 && shown.length === 0 && filter !== 'all' && (
          <p className="body">No projects with a {HUB_AIRCRAFT_TYPE_LABEL[filter]}.</p>
        )}
        <ul className="hub-cards" aria-label="Project list">
          {shown.map((p) => {
            const l = hubLines(p, finishes.get(p.id) ?? null, crew?.get(p.id) ?? null);
            const plan = plans[p.id];
            return (
              <li key={p.id} className="hub-card">
                <h3 className="heading hub-card__title">
                  <span className="data">{p.number}</span> · {p.title}
                </h3>
                <p className="caption muted">{p.subtitle}</p>
                <p className="caption">
                  <span className="chip caption">{p.statusWord}</span>{' '}
                  <span className="hub-card__type">
                    <span
                      className="hub-swatch"
                      style={{ background: `var(--${p.colour})` }}
                      aria-hidden="true"
                    />
                    {HUB_AIRCRAFT_TYPE_LABEL[p.aircraft.type]}
                  </span>
                </p>
                {typeof plan === 'string' ? (
                  <p className="caption">Could not load progress: {plan}</p>
                ) : l.progress === null ? (
                  <p className="caption muted">Loading progress…</p>
                ) : (
                  <>
                    <p className="body">
                      <strong className="data">{l.progress}</strong>
                    </p>
                    {p.hasLines && p.progress && (
                      <span className="hub-card__bar" aria-hidden="true">
                        <span
                          style={{
                            width: `${Math.min(100, p.progress.percentComplete)}%`,
                            background: `var(--${p.colour})`,
                          }}
                        />
                      </span>
                    )}
                    {l.km && <p className="caption data">{l.km}</p>}
                  </>
                )}
                <p className="caption muted">{l.report}</p>
                {l.finish && <p className="caption">{l.finish}</p>}
                {l.crew && <p className="caption">{l.crew}</p>}
                {p.box ? (
                  <Button
                    variant="quiet"
                    onClick={() => setFocus((f) => ({ box: p.box!, nonce: (f?.nonce ?? 0) + 1 }))}
                    aria-label={`Locate ${p.number} on the map`}
                  >
                    Locate on map
                  </Button>
                ) : (
                  <p className="caption muted">Not on the map yet: no block boundaries or contract areas.</p>
                )}
              </li>
            );
          })}
        </ul>
      </aside>
      {/* the legend takes the base map's colours, as the map does (decision 654) */}
      <div className="hub-stage" data-basemap={baseMapKind(base)}>
        <HubMap
          projects={shown}
          linesOf={linesOf}
          popupFor={popupFor}
          base={base}
          focus={focus}
          tracking={tracking}
          follow={follow}
          onFollowStopped={() => {
            setFollowStopped(tracking?.aircraft.find((a) => a.id === follow)?.label ?? null);
            setFollow(null);
          }}
          flyTo={flyTo}
          onZoom={setZoom}
          fires={fires.layers.features}
          fireCredits={fires.layers.credits}
          routeOf={routeOf}
          label={`Operations Hub map: ${shown.length} ${shown.length === 1 ? 'project' : 'projects'}`}
        />
        {/* the top-right column: "Base map", then "Fire on the map" under it, never over it (MB, 2 Nov 2026) */}
        <div className="hub-overlays">
          <div className="hub-overlay hub-overlay--base">
            <SelectField
              label="Base map"
              value={base}
              onChange={setBase}
              options={WEB_BASE_LAYERS.map((b) => [b.id, b.label] as const)}
            />
          </div>
          {(fires.answer || fires.error) && (
            <div className="hub-overlay hub-overlay--fires">
              <FireControls fires={fires} choice={fireChoice} onChoice={setFireChoice} />
            </div>
          )}
        </div>
        <div className="hub-overlay hub-overlay--legend">
          {outlineLegend.length > 0 && (
            <ul className="legend hub-legend" aria-label="Boundaries">
              {outlineLegend.map((e) => (
                <li key={e.word} className="legend__item caption">
                  <span
                    className={`legend__swatch legend__swatch--${e.stroke}${e.dashed ? ' legend__swatch--dashed' : ''}`}
                    style={{ borderColor: `var(--${e.colour})` }}
                    aria-hidden="true"
                  />
                  {e.word}
                </li>
              ))}
            </ul>
          )}
          {linesShown ? (
            <ul className="legend hub-legend" aria-label="Line states">
              {LINE_STATE_LEGEND.map((e) => (
                <li key={e.word} className="legend__item caption">
                  <span
                    className={`legend__swatch legend__swatch--${e.stroke}${e.dashed ? ' legend__swatch--dashed' : ''}`}
                    style={{ borderColor: `var(--${e.colour})` }}
                    aria-hidden="true"
                  />
                  {e.word}
                </li>
              ))}
            </ul>
          ) : (
            <p className="caption">Zoom in to see each line in its state.</p>
          )}
        </div>
        {sources && (
          <div className="hub-sources" role="group" aria-label="Public air traffic sources">
            <p className="caption hub-sources__total">
              Public aircraft on the map: <span className="data">{tracking?.publicCount ?? 0}</span>
            </p>
            <ul className="hub-sources__list">
              {sources.map((i) => (
                <li
                  key={i.service}
                  className={`caption hub-sources__item hub-sources__item--${i.state}`}
                  title={i.title}
                  aria-label={`${i.name}: ${i.word}, ${i.aircraft} aircraft${i.age ? `, ${i.age}` : ''}. ${i.title}`}
                >
                  <span className="hub-sources__dot" aria-hidden="true" />
                  <strong>{i.name}</strong> {i.word} · <span className="data">{i.aircraft}</span>
                  {i.age && <span className="muted"> · {i.age}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <TrackingAssetsPanel
        list={tracking?.list ?? null}
        aircraft={tracking?.aircraft ?? []}
        colours={tracking?.colours ?? new Map()}
        groups={tracking?.groups ?? []}
        shownGroups={shownGroups}
        onGroups={setShownGroups}
        hours={hours}
        onHours={setHours}
        follow={follow}
        onFollow={(id) => {
          setFollow(id);
          setFollowStopped(null);
        }}
        onLocate={(id) => setFlyTo((f) => ({ id, nonce: (f?.nonce ?? 0) + 1 }))}
        status={
          trackingError
            ? `Could not load the trackers: ${trackingError}`
            : rows
              ? null
              : 'Loading the trackers…'
        }
        followStopped={followStopped}
        accounts={rows?.accounts ?? []}
        traffic={trafficText}
      />
    </div>
  );
}
